/**
 * Mastery Level — Week 1 (W1) pure function.
 *
 * Hybrid algorithm: Leitner 5-box × Khan-style attempts + correctRate threshold.
 * Reference: docs/research/mastery-spaced-repetition-algorithms.md
 *
 * INVARIANT: pure function. No side effects. No external imports.
 * INVARIANT: 學習與遊戲分離 — do NOT import anything from src/game/.
 */

export type LeitnerBox = 1 | 2 | 3 | 4 | 5;

export type MasteryLevel = 'new' | 'practiced' | 'proficient' | 'mastered';

export interface MasteryInput {
  box: LeitnerBox;
  attempts: number;
  correctRate: number;
  /** epoch ms of last attempt; omit if never attempted. */
  lastAttemptAt?: number;
}

/** 7 days in milliseconds — staleness threshold for demotion. */
const STALE_MS = 7 * 24 * 60 * 60 * 1000; // 604_800_000

/** Now() injection point — kept as a closure so the function stays pure & testable. */
function nowMs(): number {
  return Date.now();
}

/**
 * Compute a student's mastery level for a single KP.
 *
 * Rules (in evaluation order, then staleness demote pass):
 *   - new        → attempts === 0
 *   - practiced  → attempts >= 1 && box <= 2
 *   - proficient → box >= 3 && correctRate >= 0.75
 *   - mastered   → box >= 4 && attempts >= 5 && correctRate >= 0.80
 *
 * Staleness: if lastAttemptAt is provided AND (now - lastAttemptAt) > 7 days,
 * demote one level (mastered → proficient, proficient → practiced).
 * `new` / `practiced` are not demoted further.
 *
 * @throws Error if correctRate < 0 or > 1, or box outside 1..5.
 */
export function getMasteryLevel(
  input: MasteryInput,
  now: number = nowMs(),
): MasteryLevel {
  const { box, attempts, correctRate, lastAttemptAt } = input;

  // --- invariants ---
  if (correctRate < 0 || correctRate > 1) {
    throw new Error(
      `getMasteryLevel: correctRate must be in [0, 1], got ${correctRate}`,
    );
  }
  if (!Number.isInteger(box) || box < 1 || box > 5) {
    throw new Error(
      `getMasteryLevel: box must be integer in [1, 5], got ${box}`,
    );
  }
  if (!Number.isInteger(attempts) || attempts < 0) {
    throw new Error(
      `getMasteryLevel: attempts must be non-negative integer, got ${attempts}`,
    );
  }

  // --- classify base level (highest first) ---
  let level: MasteryLevel;
  if (attempts === 0) {
    level = 'new';
  } else if (box >= 4 && attempts >= 5 && correctRate >= 0.8) {
    level = 'mastered';
  } else if (box >= 3 && correctRate >= 0.75) {
    level = 'proficient';
  } else if (attempts >= 1 && box <= 2) {
    level = 'practiced';
  } else {
    // Fallback: attempted but not meeting proficient/mastered thresholds
    // (e.g. box=3 with correctRate < 0.75) → practiced.
    level = 'practiced';
  }

  // --- staleness demote (1 step only) ---
  if (
    lastAttemptAt !== undefined &&
    level !== 'new' &&
    now - lastAttemptAt > STALE_MS
  ) {
    if (level === 'mastered') level = 'proficient';
    else if (level === 'proficient') level = 'practiced';
    // practiced stays practiced
  }

  return level;
}
